import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Breadcrumbs,
} from "@mui/material";
import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { useQuery } from "@tanstack/react-query";
import { fetchProductsByCategory } from "../services/productService";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const CategoryProducts = () => {
  const { category } = useParams();
  const navigate = useNavigate();

  // Format the category for display (capitalize first letter)
  const displayCategory = category.charAt(0).toUpperCase() + category.slice(1);

  /**
   * React Query Hook for Category Products
   *
   * This hook fetches products filtered by category and caches them for 5 minutes.
   * It eliminates duplicate API calls that were occurring in the previous implementation.
   *
   * The query key includes the category parameter to ensure different categories
   * are cached separately, while still benefiting from deduplication when the same
   * category is requested multiple times.
   *
   * This implementation works with the SEO-friendly slugs created in our previous
   * optimization, ensuring consistent URL structure throughout the application.
   *
   * See docs/react-query-implementation.md for more details
   */
  const {
    data: products = [], // Default to empty array if data is undefined
    isLoading, // Loading state managed by React Query
    error, // Error state managed by React Query
  } = useQuery({
    queryKey: ["products", "category", category], // Unique key including the category parameter
    queryFn: () => fetchProductsByCategory(category),
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
  });

  // Update meta tags directly when component mounts or category changes
  useEffect(() => {
    // Only update meta tags if we have products data
    if (!isLoading && products) {
      // Update meta tags for SEO
      updateMetaTags({
        title: `${displayCategory} Leather Products | KBros Leather`,
        description: `Shop our collection of handcrafted ${displayCategory.toLowerCase()} leather products. Each piece is made with premium materials and customizable with personalized engravings.`,
        keywords: `leather ${displayCategory.toLowerCase()}, handcrafted leather, personalized leather gifts, custom engraving, ${displayCategory.toLowerCase()} accessories`,
        url: `${window.location.origin}/products/category/${category}`,
        structuredData: [generateStructuredData(), generateBreadcrumbData()],
      });
    }
  }, [category, displayCategory, products, isLoading]);

  if (isLoading) {
    return (
      <Box
        sx={{
          padding: 4,
          backgroundColor: "#f5f5f5",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading products...
        </Typography>
      </Box>
    );
  }

  // Generate structured data for category product listing
  const generateStructuredData = () => {
    const itemListElements = [];

    products.forEach((product, index) => {
      itemListElements.push({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.name,
          description:
            product.shortDescription ||
            product.longDescription?.substring(0, 160) ||
            "",
          image:
            product.images?.[0]?.url ||
            product.thumbnail ||
            "",
          url: `${window.location.origin}/products/${product.slug || product.id}`,
          offers: {
            "@type": "Offer",
            price: product.price,
            priceCurrency: "USD",
            availability:
              product.baseStockCount > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
          },
        },
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: itemListElements,
    };
  };

  // Generate breadcrumb structured data
  const generateBreadcrumbData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${window.location.origin}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Products",
          item: `${window.location.origin}/products`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: displayCategory,
          item: window.location.href,
        },
      ],
    };
  };

  // Function to directly update meta tags
  const updateMetaTags = (metaData) => {
    // Update title
    document.title = metaData.title;

    // Update description
    let descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute("content", metaData.description);
    } else {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      descriptionTag.setAttribute("content", metaData.description);
      document.head.appendChild(descriptionTag);
    }

    // Update keywords
    let keywordsTag = document.querySelector('meta[name="keywords"]');
    if (keywordsTag) {
      keywordsTag.setAttribute("content", metaData.keywords);
    } else {
      keywordsTag = document.createElement("meta");
      keywordsTag.setAttribute("name", "keywords");
      keywordsTag.setAttribute("content", metaData.keywords);
      document.head.appendChild(keywordsTag);
    }

    // Update canonical link
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute("href", metaData.url);
    } else {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      canonicalTag.setAttribute("href", metaData.url);
      document.head.appendChild(canonicalTag);
    }

    // Update Open Graph tags
    updateOpenGraphTag("og:title", metaData.title);
    updateOpenGraphTag("og:description", metaData.description);
    updateOpenGraphTag("og:type", "website");
    updateOpenGraphTag("og:url", window.location.href);
    updateOpenGraphTag("og:site_name", "KBros Leather");

    // Update structured data
    if (metaData.structuredData) {
      // Remove existing structured data scripts
      const existingScripts = document.querySelectorAll(
        'script[type="application/ld+json"]',
      );
      existingScripts.forEach((script) => script.remove());

      // Add new structured data scripts
      if (Array.isArray(metaData.structuredData)) {
        metaData.structuredData.forEach((data) => {
          const scriptTag = document.createElement("script");
          scriptTag.setAttribute("type", "application/ld+json");
          scriptTag.textContent = JSON.stringify(data);
          document.head.appendChild(scriptTag);
        });
      } else {
        const scriptTag = document.createElement("script");
        scriptTag.setAttribute("type", "application/ld+json");
        scriptTag.textContent = JSON.stringify(metaData.structuredData);
        document.head.appendChild(scriptTag);
      }
    }
  };

  // Helper function to update Open Graph tags
  const updateOpenGraphTag = (property, content) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (tag) {
      tag.setAttribute("content", content);
    } else {
      tag = document.createElement("meta");
      tag.setAttribute("property", property);
      tag.setAttribute("content", content);
      document.head.appendChild(tag);
    }
  };

  return (
    <Box sx={{ padding: 4, backgroundColor: "#f5f5f5" }}>
      {/* SEO Meta Tags are now handled directly via DOM manipulation */}
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
          Home
        </Link>
        <Link
          to="/products"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Products
        </Link>
        <Typography color="text.primary">{displayCategory}</Typography>
      </Breadcrumbs>

      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{ marginBottom: 4 }}
      >
        {displayCategory}
      </Typography>

      {error ? (
        <Box sx={{ textAlign: "center", my: 4 }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error instanceof Error
              ? error.message
              : "Failed to load products. Please try again later."}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/products")}
            sx={{ mt: 2 }}
          >
            View All Products
          </Button>
        </Box>
      ) : products.length === 0 ? (
        <Box sx={{ textAlign: "center", my: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No products in this category yet.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/products")}
            sx={{ mt: 2 }}
          >
            View All Products
          </Button>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CategoryProducts;
